// Geboortevenster · Vertaling: pure payloads → tekst met humanDesign.properties.*, PHS-content en kruisnamen; verschilnotatie voor lijsten.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { formatHdProfileValue } from '../bodygraph/basic-properties/hd-chart-properties.util';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPhsInfoService } from '../bodygraph/hd-phs-info.service';
import {
  HdBirthWindowListDiff,
  HdBirthWindowPayload,
  HdBirthWindowPropertyKey,
  HdBirthWindowTier,
} from './birth-window.models';

const P = 'humanDesign.properties';
const BW = 'humanDesign.chart.birthWindow';

/** Eén gekleurd deel van een bloklabel (verschilnotatie: + groen, − rood, rest neutraal). */
export interface HdBirthWindowLabelPart {
  readonly text: string;
  readonly tone: 'neutral' | 'added' | 'removed' | 'muted';
}

/**
 * Zet de pure payloads van de builders om naar tekst, met dezelfde bronnen als
 * de chart zelf: `humanDesign.properties.*`, de PHS-content en de kruisnamen.
 */
@Injectable({ providedIn: 'root' })
export class HdBirthWindowLabelService {
  private readonly transloco = inject(TranslocoService);
  private readonly phsInfo = inject(HdPhsInfoService);
  private readonly gateInfo = inject(HdGateInfoService);

  private t(key: string, lang: string, params?: Record<string, unknown>): string {
    return this.transloco.translate<string>(key, params, lang);
  }

  propertyLabel(key: HdBirthWindowPropertyKey, lang: string): string {
    return this.t(`${BW}.properties.${key}`, lang);
  }

  tierLabel(tier: HdBirthWindowTier, lang: string): string {
    return this.t(`${BW}.tiers.${tier}`, lang);
  }

  /** Uitleg van een laag; het momentvenster (bewaard moment) heeft een eigen uitleg voor Basics en Planeten. */
  tierHint(tier: HdBirthWindowTier, lang: string, overlay = false): string {
    return this.t(overlay ? `${BW}.moment.tierHints.${tier}` : `${BW}.tierHints.${tier}`, lang);
  }

  listItemLabel(itemKind: 'center' | 'channel', item: string, lang: string): string {
    return itemKind === 'center' ? this.t(`${P}.centerNames.${item}`, lang) : item;
  }

  /** Compacte tekst voor een blok in de lane. */
  valueLabel(payload: HdBirthWindowPayload, lang: string): string {
    switch (payload.kind) {
      case 'none':
        return '';
      case 'type': {
        const base = this.t(`${P}.typeValues.${payload.base}`, lang);
        const modifier = payload.manifesting ? `${this.t(`${P}.typeValues.manifesting`, lang)} ` : '';
        const pure = payload.pure ? `${this.t(`${P}.purePrefix`, lang)} ` : '';
        return `${pure}${modifier}${base}`.trim();
      }
      case 'i18n':
        return this.t(`${P}.${payload.key}`, lang);
      case 'text':
        return payload.text;
      case 'profile':
        return `${payload.personalityLine}/${payload.designLine}`;
      case 'cross': {
        const [pSun, pEarth, dSun, dEarth] = payload.gates;
        const gates = `${pSun}/${pEarth} | ${dSun}/${dEarth}`;
        const name = payload.nameKey ? this.gateInfo.crossNameIn(payload.nameKey, lang) : '';
        return name ? `${name} (${gates})` : `${this.t(`${P}.crossAngleValues.${payload.angleKey}`, lang)} (${gates})`;
      }
      case 'list':
        return payload.items.map(item => this.listItemLabel(payload.itemKind, item, lang)).join(', ');
      case 'phs': {
        const info = this.phsInfo.categoryInfoIn(payload.category, lang);
        if (payload.part === 'color') return info.colors[payload.color - 1]?.name ?? String(payload.color);
        return info.cognition[payload.tone - 1]?.name ?? String(payload.tone);
      }
      case 'base':
        return this.t(`${BW}.baseValue`, lang, { base: payload.base });
    }
  }

  planetLabel(sourceKey: string, lang: string): string {
    return this.t(`${BW}.planets.${sourceKey}`, lang);
  }

  /** Uitgebreide tekst voor de popup (profielnamen, volledige kruisnaam, kleur én toon). */
  valueLabelFull(payload: HdBirthWindowPayload, lang: string): string {
    switch (payload.kind) {
      case 'profile':
        return formatHdProfileValue(
          { personalityLine: payload.personalityLine, designLine: payload.designLine },
          key => this.t(`${P}.${key}`, lang),
        );
      case 'cross': {
        const [pSun, pEarth, dSun, dEarth] = payload.gates;
        const angle = this.t(`${P}.crossAngleValues.${payload.angleKey}`, lang);
        const gates = `(${pSun}/${pEarth} | ${dSun}/${dEarth})`;
        if (!payload.nameKey) return `${angle} ${gates}`;
        return `${angle} ${this.gateInfo.crossNameConnectorIn(lang)} ${this.gateInfo.crossNameIn(payload.nameKey, lang)} ${gates}`;
      }
      case 'phs': {
        const info = this.phsInfo.categoryInfoIn(payload.category, lang);
        const color = info.colors[payload.color - 1]?.name ?? String(payload.color);
        const tone = info.cognition[payload.tone - 1]?.name ?? String(payload.tone);
        return payload.part === 'color' ? `${color} (${payload.color})` : `${tone} (${payload.color}.${payload.tone})`;
      }
      default:
        return this.valueLabel(payload, lang);
    }
  }

  /**
   * Verschilnotatie voor lijsten t.o.v. de geboortewaarde: "+ 20-34 · − 57-34",
   * met het ongewijzigde deel optioneel gedempt erachter (besluit 7).
   */
  diffParts(diff: HdBirthWindowListDiff, itemKind: 'center' | 'channel', lang: string, includeUnchanged: boolean): HdBirthWindowLabelPart[] {
    const parts: HdBirthWindowLabelPart[] = [];
    const label = (item: string) => this.listItemLabel(itemKind, item, lang);
    if (diff.added.length > 0) parts.push({ text: `+ ${diff.added.map(label).join(', ')}`, tone: 'added' });
    if (diff.removed.length > 0) parts.push({ text: `− ${diff.removed.map(label).join(', ')}`, tone: 'removed' });
    if (includeUnchanged && diff.unchanged.length > 0) parts.push({ text: diff.unchanged.map(label).join(', '), tone: 'muted' });
    if (parts.length === 0) parts.push({ text: this.t(`${BW}.unchanged`, lang), tone: 'muted' });
    return parts;
  }
}
